import type {
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

import { cn } from '@/shared/utils/cn';

/**
 * The tables of the app, all alike: a scroll region that the keyboard can reach, headers
 * in small caps, numbers right aligned with `.num`, rows separated by a hairline.
 *
 * `minWidth` is the width under which the table scrolls sideways instead of squeezing.
 * Desktop first: a listing has to fit at 1280 px without scrolling, so pass a width the
 * columns actually need (or `"0"`) and turn the rows into `CardList` items under `md`.
 */
export function Table({
  label,
  minWidth = '40rem',
  className,
  children,
}: {
  /** What the table is, for the scroll region and screen readers. */
  label: string;
  minWidth?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    // Focusable, so the columns on the right are reachable with the keyboard when the
    // table has to scroll sideways. A scroll container only a mouse can move is WCAG 2.1.1.
    <div className={cn('overflow-x-auto', className)} tabIndex={0} role="region" aria-label={label}>
      <table className="w-full text-left text-[14px]" style={{ minWidth }}>
        {children}
      </table>
    </div>
  );
}

export function THead({
  sticky = false,
  children,
}: {
  /** Keeps the header row visible while a long table scrolls under it. */
  sticky?: boolean;
  children: ReactNode;
}) {
  return (
    <thead
      className={cn(
        'text-ink-3 text-[11.5px] font-semibold tracking-[0.08em] uppercase',
        sticky && 'bg-card sticky top-0 z-10',
      )}
    >
      {children}
    </thead>
  );
}

export function Th({ className, children, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn('border-line-2 border-b px-3 py-2 font-semibold', className)}
      {...props}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-line-2 divide-y">{children}</tbody>;
}

/**
 * A row. With `interactive`, the whole row is the link to its detail: pointer cursor,
 * hover, a place in the tab order, and Enter or Space fire `onClick` exactly like a
 * click. A button inside the row keeps working on its own: the keys only count when the
 * focus is on the row itself, and a click on the button reaches `onClick` through the
 * normal bubbling, so the button stops it (`event.stopPropagation()`) if it must not.
 */
export function Tr({
  interactive = false,
  className,
  children,
  onClick,
  onKeyDown,
  tabIndex,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  function handleKeyDown(event: KeyboardEvent<HTMLTableRowElement>) {
    onKeyDown?.(event);
    if (!interactive || !onClick || event.defaultPrevented) return;
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }
  return (
    <tr
      className={cn(
        'align-top',
        interactive &&
          'hover:bg-paper-2/60 focus-visible:outline-accent cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
        className,
      )}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : onKeyDown}
      tabIndex={tabIndex ?? (interactive ? 0 : undefined)}
      {...props}
    >
      {children}
    </tr>
  );
}

export function Td({ className, children, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('px-3 py-2', className)} {...props}>
      {children}
    </td>
  );
}
